















































































































































import { debounceFn } from 'debounce-decorator-ts';
import { Component, Inject, Vue, Watch } from 'vue-property-decorator';

import Indicators, { Indicator, Rating } from '@solucx/indicators';

// App Style
import '../assets/css/base.css';

// Form Components
import BackButton from '@/components/BackButton.vue';
import CustomStyle from '@/components/CustomStyle.vue';
import Enrichment from '../components/Enrichment.vue';
import Anonym from '../components/Form/Anonym.vue';
import Classes from '../components/Form/Classes/Classes.vue';
import Comment from '../components/Form/Comment.vue';
import IntroText from '../components/IntroText.vue';

// States
import Error from '../components/Error.vue';

// Score Input
import ScoreInput from '../components/ScoreInput/ScoreInput.vue';

// Components
import AccentStyleCreator from '../services/AccentStyleCreator';
import SoluCXService from '../services/SolucxService';
import UrlParsedInfo from '../services/UrlParsedInfo';

// Models
import JourneyParameters from '../models/interfaces/JourneyParameters';
import RatingSubmitResponse from '../models/interfaces/RatingSubmitResponse';
import SurveySettings from '../models/interfaces/SurveySettings';
import SurveyAnswer from '../models/SurveyAnswer';
import { WidgetState } from './WidgetState';

// Views
import AcceptanceContactIfDetractor from '@/components/AcceptanceContactIfDetractor.vue';
import ContactChannelCheckboxes from '@/components/ContactChannelCheckboxes.vue';
import AcceptanceContactIfNeutral from '@/components/AcceptanceContactIfNeutral.vue';
import AcceptanceContactIfPromoter from '@/components/AcceptanceContactIfPromoter.vue';
import { getCookie, setCookie } from '@/services/CookiesService';
import Source from '@/services/Source';
import CompleteView from '../Views/CompleteView.vue';
import ErrorView from '../Views/ErrorView.vue';
import LoadingView from '../Views/LoadingView.vue';
import PendingView from '../Views/PendingView.vue';
import SurveyErrorHandler from './SurveyErrorHandler';

/**
 * Classe principal da aplicação Survey.
 * Esta classe faz as requisições com a API e controla o estado da aplicação
 */
@Component({
  components: {
    CustomStyle,
    ScoreInput,
    IntroText,
    Classes,
    Comment,
    Anonym,
    ErrorView,
    CompleteView,
    PendingView,
    LoadingView,
    Error,
    Enrichment,
    BackButton,
    AcceptanceContactIfDetractor,
    ContactChannelCheckboxes,
    AcceptanceContactIfNeutral,
    AcceptanceContactIfPromoter,
  },
})
export default class Widget extends Vue {
  @Inject() public readonly soluCXService: SoluCXService;
  @Inject() public readonly urlParsedInfo: UrlParsedInfo;

  public state: WidgetState = null;
  public rating: SurveyAnswer = new SurveyAnswer();
  public sending: boolean = false;
  public surveyRequestError: string = null;
  public surveyResponse: RatingSubmitResponse = null;
  public isCommentRequiredAndHasNoComments: boolean = false;
  public isSurveyCompleted: boolean = false;
  public isTransitioning: boolean = false;
  public config: SurveySettings = {
    transactionId: null,
    attemptId: null,
    storeId: null,
    employeeId: null,
    journeyId: null,
    journeyType: null,
    classes: null,
    instance: null,
    parameters: null,
    errors: null,
  };

  public parameters: JourneyParameters = {};

  public whiteMode = false;
  private cookieQuarentinePrefix: string = 'alreadyAnswered_';
  private transitionLockTimeout = 0;

  public get isJourneyTypeNone(): boolean {
    return this.config.journeyType === 20;
  }

  public get shouldAskEnrichment(): boolean {
    return !!this.parameters.showEmailEnrichment && !this.rating.anonymous;
  }

  public get sendButtonText() {
    return this.parameters.sendButtonText ?? 'ENVIAR';
  }

  public get isDetractor(): boolean {
    const kpiInstance: Indicator = Indicators.getIndicator(this.rating.journeyType);
    return kpiInstance.getRating(this.rating.score) === Rating.NEGATIVE;
  }

  public get commentLabel(): string {
    const hasPositivelabel: boolean = !!this.parameters.commentLabel;
    const hasNegativeLabel: boolean = !!this.parameters.commentNegativeLabel;

    if (hasPositivelabel && !this.isDetractor) return this.parameters.commentLabel;
    if (hasNegativeLabel && this.isDetractor) return this.parameters.commentNegativeLabel;
    if (hasPositivelabel) return this.parameters.commentLabel;
    else return this.$tc('howCouldWeImprove');
  }

  public get isAllLikesAndDislikesItemsSelected(): boolean {
    return this.rating.items.every((element) => element.like !== null);
  }

  public get resizeWidgetState() {
    return {
      score: () => this.setScoreHeight(),
      likedislike: () => this.setLikeDislikeHeight(),
      comment: () => this.resizeSoluCXWidgetTo('250'),
      error: () => this.resizeSoluCXWidgetTo('400'),
      default: () => this.resizeSoluCXWidgetTo('180'),
    };
  }

  public handleAfterEnter(element: HTMLElement): void {
    this.unlockAfterTransition();
    if (element?.matches('.widget-step, .widget-form')) {
      this.$nextTick(() => element.focus());
    }
  }

  public async mounted() {
    this.state = WidgetState.loading;
    this.isSurveyCompleted = false;
    try {
      // set Widget channel
      this.urlParsedInfo.source = Source.widget;

      // Get configuration from URL QueryString and params data
      this.config = await this.soluCXService.getSurveySettings(this.urlParsedInfo);
      this.parameters = this.config.parameters;

      // Check budget for current store
      try {
        const hasBudget = await this.soluCXService.checkBudget(this.config.transactionId);
        if (hasBudget === false) {
          parent.postMessage('dismissSoluCXWidget', '*');
          return;
        }
      } catch (e) {
        parent.postMessage('dismissSoluCXWidget', '*');
        return;
      }

      // Create survey based on params
      this.rating = new SurveyAnswer(
        this.config.transactionId,
        this.config.attemptId,
        this.config.storeId,
        this.config.employeeId,
        this.urlParsedInfo.rating,
        this.config.classes,
        this.config.journeyType,
      );

      this.rating.journeyParameters = this.config?.parameters;

      // set Widget channel
      this.rating.source = Source.widget;

      this.rating.setAcceptanceContactDefaultValue(this.parameters);

      // After loading survey , then send message to open the widget
      parent.postMessage('openSoluCXWidget', '*');
      parent.postMessage(`openSoluCXWidgetIds-${this.config.transactionId}-${this.config.attemptId}`, '*');

      // go to "score" state
      this.state = this.getNextState();

      this.setStateWhenJourneyTypeNone();
    } catch (e) {
      parent.postMessage('dismissSoluCXWidget', '*');
      this.state = WidgetState.error;
      if (e.errors && typeof e.errors[0] === 'string') this.surveyRequestError = e.errors[0];
      else this.surveyRequestError = e;
      // tslint:disable-next-line
      console.error(this.surveyRequestError);
    }
  }

  @Watch('rating.acceptanceContactIfDetractor')
  public acceptanceContactIfDetractorChanged() {
    if (this.rating.acceptanceContactIfDetractor) {
      this.resizeSoluCXWidgetTo('310');
    }
  }

  @Watch('rating.acceptanceContactIfNeutral')
  public acceptanceContactIfNeutralChanged() {
    if (this.rating.acceptanceContactIfNeutral) {
      this.resizeSoluCXWidgetTo('310');
    }
  }

  @Watch('rating.acceptanceContactIfPromoter')
  public acceptanceContactIfPromoterChanged() {
    if (this.rating.acceptanceContactIfPromoter) {
      this.resizeSoluCXWidgetTo('310');
    }
  }

  public setPropertiesDefaultValue(): void {
    this.setStateWhenJourneyTypeNone();
    this.rating.setAcceptanceContactDefaultValue(this.parameters);
  }

  public setStateWhenJourneyTypeNone() {
    if (this.isJourneyTypeNone) {
      this.state = this.getNextState();
      this.rating.score = 0;
    }
  }

  // Debounce allows user to tap several times before sending the data
  @debounceFn(1000, 'onScoreTimeout')
  public async onScore() {
    if (this.state !== WidgetState.score) return;
    try {
      this.rating.setAcceptanceContactDefaultValue(this.parameters);
      this.validateCookieQuarantine();
      await this.soluCXService.postPartialSurvey(this.rating, {}, 'widget-score');
      if (this.state !== WidgetState.score) return;
      this.state = this.getNextState();
      parent.postMessage('partialSoluCXWidget', '*');
    } catch (error) {
      const errors = error?.response?.data?.errors || [];
      this.handleRequestError(errors);
    }
  }

  public async onClassesPartial() {
    try {
      this.soluCXService.postPartialSurvey(this.rating, {}, 'widget-classes');
    } catch (e) {
      this.state = WidgetState.error;
      this.surveyRequestError = e;
    }
  }

  // Debounce allows user to tap several times before sending the data
  @debounceFn(1000, 'onClassesTimeout')
  public async onClasses() {
    this.state = this.getNextState();
  }

  public async onComment() {
    this.state = this.getNextState();
  }

  public getJourneyId(): string {
    return this.config?.journeyId.toString();
  }

  public applyCookieQuarantine(): boolean {
    const journey: string = this.getJourneyId();
    const hasCookie: boolean = getCookie(`${this.cookieQuarentinePrefix}${journey}`) === journey;
    return Boolean(hasCookie && this.parameters.enableCookieQuarantine);
  }

  public validateCookieQuarantine(): boolean {
    return (this.rating.isBlockedByCookieQuarantine = this.applyCookieQuarantine() ?? false);
  }

  public lockDuringTransition(): void {
    this.isTransitioning = true;
    clearTimeout(this.transitionLockTimeout);
    this.transitionLockTimeout = window.setTimeout(() => {
      this.isTransitioning = false;
    }, 800);
  }

  public unlockAfterTransition(): void {
    clearTimeout(this.transitionLockTimeout);
    this.isTransitioning = false;
  }

  public checkRequiredFieldsAndSubmitSurvey(): void {
    if (this.isTransitioning) return;
    this.validateCommentaryField();

    if (this.isCommentRequiredAndHasNoComments) return;

    this.onSubmit();
  }

  public validateCommentaryField(): void {
    this.isCommentRequiredAndHasNoComments =
      this.parameters.isCommentRequired && (!this.rating.comment || this.rating.comment?.trim().length === 0);
  }

  public async onSubmit() {
    if (this.sending || this.isSurveyCompleted) {
      return;
    }

    this.sending = true;

    try {
      this.surveyResponse = await this.soluCXService.postSurveyRating(this.rating);

      if (this.parameters?.showThanksScreen === false) {
        this.state = WidgetState.sending;
      } else {
        this.state = this.getNextState();
      }

      // apply Cookie quarantine
      this.applyCookieQuarantine();

      if (this.parameters?.enableCookieQuarantine) {
        const domain: string = window.location.hostname === 'localhost' ? 'localhost' : '.solucx.com.br';
        const journey: string = this.getJourneyId();
        setCookie(`${this.cookieQuarentinePrefix}${journey}`, journey, 1, domain);
      }

      parent.postMessage('completeSoluCXWidget', '*');

      this.isSurveyCompleted = true;

      if (this.parameters?.showThanksScreen === false) {
        parent.postMessage('closeSoluCXWidget', '*');
      } else {
        setTimeout(() => {
          parent.postMessage('closeSoluCXWidget', '*');
        }, 10000);
      }
    } catch (error) {
      const errors = error?.response?.data?.errors || [];
      this.handleRequestError(errors);
    } finally {
      this.sending = false;
    }
  }

  public getNextState(): WidgetState {
    if (this.state === WidgetState.done) return WidgetState.done;

    if (this.state === WidgetState.loading) {
      return WidgetState.score;
    }

    if (this.state === WidgetState.score) {
      if (this.parameters.showClasses && this.config.classes?.length > 0 && !this.isAllLikesAndDislikesItemsSelected)
        return WidgetState.likedislike;
      if (this.parameters.showComment) return WidgetState.comment;
      if (this.shouldAskEnrichment) return WidgetState.erichment;
      return WidgetState.done;
    }

    if (this.state === WidgetState.likedislike) {
      if (this.parameters.showComment) return WidgetState.comment;
      if (this.shouldAskEnrichment) return WidgetState.erichment;
      return WidgetState.done;
    }

    if (this.state === WidgetState.comment) {
      if (this.shouldAskEnrichment) return WidgetState.erichment;
      return WidgetState.done;
    }

    if (this.state === WidgetState.erichment) {
      return WidgetState.done;
    }

    if (this.state === WidgetState.sending) {
      return WidgetState.done;
    }
  }

  public getBackState(): WidgetState {
    if (this.state === WidgetState.likedislike) return WidgetState.score;

    if (this.state === WidgetState.comment) {
      if (this.parameters.showClasses && this.config.classes?.length > 0) return WidgetState.likedislike;
      return WidgetState.score;
    }
  }

  public onBack(): void {
    this.resetCommentSettings();
    this.state = this.getBackState();
  }

  public resetCommentSettings(): void {
    if (this.state === WidgetState.comment) {
      this.rating.acceptanceContactIfDetractor = null;
      this.rating.acceptanceContactIfNeutral = null;
      this.rating.acceptanceContactIfPromoter = null;
      this.rating.acceptanceChannels.email = false;
      this.rating.acceptanceChannels.whatsapp = false;
      this.rating.acceptanceChannels.phone = false;
    }
  }

  public handleRequestError(errors: string[]) {
    this.state = WidgetState.error;

    const foundError = new SurveyErrorHandler({ errors }).handle();
    this.surveyRequestError = foundError.state;

    setTimeout(() => {
      this.rating.score = null;
      parent.postMessage('closeSoluCXWidget', '*');
    }, 3000);
  }

  public resizeSoluCXWidgetTo(height: string): void {
    document.body.style.height = `${height}px`;
    parent.postMessage(`resizeSoluCXWidget-${height}`, '*');
  }

  public setScoreHeight(): void {
    const isEmoticonDisplayAndCsatInput: boolean =
      this.parameters.displayType === 'emoticon' && this.parameters.inputType === 'csat';

    isEmoticonDisplayAndCsatInput ? this.resizeSoluCXWidgetTo('200') : this.resizeSoluCXWidgetTo('180');
  }

  public setLikeDislikeHeight(): void {
    setTimeout(() => {
      const w: number = document.body.scrollHeight;
      parent.postMessage(`resizeSoluCXWidget-${w}`, '*');
    }, 500);
  }

  public async handleAutoCompleteSurvey(state: WidgetState): Promise<void> {
    if (state === WidgetState.done) {
      await this.onSubmit();
    }
  }

  @Watch('state')
  public async onStatechanged(updatedState: WidgetState) {
    // changes sizes based on state
    if (!this.parameters) return;

    await this.handleAutoCompleteSurvey(updatedState);

    Object.keys(this.resizeWidgetState).map((state) => {
      if (updatedState !== WidgetState[state]) return;
      this.resizeWidgetState[state]();
    });
  }

  @Watch('parameters.color')
  public onColorchanged(val: string, old: string) {
    // change color based on intance
    AccentStyleCreator.createAccentColorStyle(val);
  }

  @Watch('rating.comment')
  public updateCommentaryFieldValidation(): void {
    this.validateCommentaryField();
  }
}
